// The client's response to "something changed on the server" (GitHub issue #3265).
//
// Both the hashes-changed ping and a reconnect mean the same thing - run the ordinary music update
// check now rather than waiting to notice - and the walk itself does the rest: it compares the music
// hashes and, through the bundle hash it reads from the same response, starts the service-worker
// update that reloads the app once the playback gate opens.
//
// Two things are deliberately between the trigger and the walk:
//
//   - jitter. Every client of one server receives a ping in the same instant (the server's own
//     throttle is a second), and a reconnect herd arrives together after a deploy, so each client
//     waits a random moment first and the walk's fetches spread out instead of landing at once.
//   - coalescing. A second trigger inside the jitter window does not arm a second timer; the
//     detector can legitimately send two messages for one save (a song-list edit pings and also
//     sends update-weekly-song-lists), and refreshMusicCache() already coalesces walks in flight.
import { getCurrentOrg, synchronizeWeeklySongLists } from '../data/use_current_org.tsx';
import { refreshMusicCache } from './music_cache_refresh.ts';

// small enough that an update still feels instant, large enough to spread a congregation's worth of
// clients across it
export const liveUpdateRefreshJitterMs = 3_000;

export interface LiveUpdateRefreshDependencies {
  random?: () => number;
  refresh?: () => void;
  // a reconnect also resyncs the current org's weekly song lists; the flag is remembered across a
  // pending timer, so a ping that arrived first does not drop the reconnect's sync
  syncWeeklySongLists?: boolean;
}

let timer: ReturnType<typeof setTimeout> | undefined;
let pendingWeeklySync = false;

// Runs the music update check after a random delay in [0, liveUpdateRefreshJitterMs). A no-op while
// a delayed refresh is already pending (apart from remembering the weekly-list flag).
export function scheduleLiveUpdateRefresh(dependencies: LiveUpdateRefreshDependencies = {}): void {
  pendingWeeklySync ||= dependencies.syncWeeklySongLists ?? false;
  if (timer !== undefined) {
    return;
  }
  const random = dependencies.random ?? Math.random;
  const refresh = dependencies.refresh ?? runMusicCacheRefresh;
  timer = setTimeout(() => {
    timer = undefined;
    const syncWeekly = pendingWeeklySync;
    pendingWeeklySync = false;
    refresh();
    if (syncWeekly && getCurrentOrg()) {
      void synchronizeWeeklySongLists();
    }
  }, random() * liveUpdateRefreshJitterMs);
}

function runMusicCacheRefresh(): void {
  // explicit, so the ambient throttle never swallows a change the server just told us about
  void refreshMusicCache({ trigger: 'explicit' });
}

// test-only: the pending timer is module state for the life of the app
export function resetLiveUpdateRefreshForTests(): void {
  clearTimeout(timer);
  timer = undefined;
  pendingWeeklySync = false;
}
